import { CreateButton, List, useTable } from "@refinedev/antd";
import { useList, useNavigation } from "@refinedev/core";
import {
  Button,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";

import { AppImagePreview } from "../../../components";
import {
  TV_FORMAT_LABELS,
  TV_KIND_LABELS,
  TV_LEAGUES,
  TV_STATUS_COLORS,
  TV_STATUS_LABELS,
  TvShow,
  TvVideo,
  toOptions,
  tvErrorNotification,
} from "../Lib";
import { matchLabel, TvDelete } from "../Components";

type Filters = {
  q?: string;
  kind?: string;
  status?: string;
  format?: string;
  source?: string;
  leagueId?: number;
  showId?: number;
};

/** Опубликовано, но с датой в будущем: на сайте появится только в это время. */
const statusTag = (v: TvVideo) =>
  v.status === "PUBLISHED" &&
  v.publishedAt &&
  dayjs(v.publishedAt).isAfter(dayjs()) ? (
    <Tag color="gold">
      Отложено до {dayjs(v.publishedAt).format("DD.MM HH:mm")}
    </Tag>
  ) : (
    <Tag color={TV_STATUS_COLORS[v.status]}>{TV_STATUS_LABELS[v.status]}</Tag>
  );

export const TvVideosPage = () => {
  const { push } = useNavigation();
  const { tableProps, setFilters, tableQueryResult } = useTable<TvVideo>({
    resource: "tv-videos",
    dataProviderName: "tvDataProvider",
    syncWithLocation: false,
    pagination: { pageSize: 20 },
    filters: { mode: "server" },
    ...tvErrorNotification("Не удалось загрузить"),
  });

  const { data: showsData } = useList<TvShow>({
    resource: "tv-shows",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });

  const applyFilters = (values: Filters) =>
    setFilters(
      Object.entries(values).map(([field, value]) => ({
        field,
        operator: "eq" as const,
        value: typeof value === "string" ? value.trim() || undefined : value,
      })),
      "replace"
    );

  return (
    <List
      title="ФНЛ ТВ — видео"
      resource="tv-videos"
      breadcrumb={false}
      canCreate={false}
      headerButtons={
        <CreateButton resource="tv-videos">Добавить видео</CreateButton>
      }
    >
      <Form<Filters>
        layout="inline"
        onFinish={applyFilters}
        style={{ marginBottom: 16, rowGap: 8 }}
      >
        <Form.Item name="q">
          <Input.Search
            allowClear
            placeholder="Название или номер"
            style={{ width: 240 }}
            onSearch={() => undefined}
          />
        </Form.Item>
        <Form.Item name="kind">
          <Select
            allowClear
            placeholder="Тип"
            style={{ width: 190 }}
            options={toOptions(TV_KIND_LABELS)}
          />
        </Form.Item>
        <Form.Item name="status">
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: 150 }}
            options={toOptions(TV_STATUS_LABELS)}
          />
        </Form.Item>
        <Form.Item name="leagueId">
          <Select
            allowClear
            placeholder="Лига"
            style={{ width: 210 }}
            options={TV_LEAGUES}
          />
        </Form.Item>
        <Form.Item name="format">
          <Select
            allowClear
            placeholder="Формат"
            style={{ width: 170 }}
            options={toOptions(TV_FORMAT_LABELS)}
          />
        </Form.Item>
        <Form.Item name="showId">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Шоу"
            style={{ width: 210 }}
            options={(showsData?.data ?? []).map((s) => ({
              value: s.id,
              label: s.title,
            }))}
          />
        </Form.Item>
        <Form.Item name="source">
          <Select
            allowClear
            placeholder="Откуда"
            style={{ width: 190 }}
            options={[
              { value: "MANUAL", label: "Заведено вручную" },
              { value: "AUTO", label: "Перенесено автоматически" },
            ]}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit">
          Показать
        </Button>
      </Form>

      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 1300 }}
        pagination={{
          ...tableProps.pagination,
          showTotal: (total) => `Всего ${total}`,
        }}
        locale={{
          emptyText: (
            <div style={{ margin: 48 }}>
              {tableQueryResult.isError
                ? "Не удалось загрузить видео"
                : "Видео не найдены"}
            </div>
          ),
        }}
      >
        <Table.Column<TvVideo> title="№" dataIndex="id" width={80} />
        <Table.Column<TvVideo>
          title="Обложка"
          width={140}
          render={(_, v) =>
            v.coverUrl ? (
              <AppImagePreview
                imageURL={v.coverUrl}
                useS3={!v.coverUrl.startsWith("http")}
              />
            ) : (
              <Typography.Text type="secondary">нет</Typography.Text>
            )
          }
        />
        <Table.Column<TvVideo>
          title="Видео"
          render={(_, v) => (
            <Space direction="vertical" size={4}>
              <Typography.Text strong>{v.title}</Typography.Text>
              <Space size={4} wrap>
                <Tag>{TV_KIND_LABELS[v.kind]}</Tag>
                {v.format === "VERTICAL" && (
                  <Tag color="purple">Вертикальное</Tag>
                )}
                {v.source === "AUTO" && <Tag color="blue">Авто</Tag>}
              </Space>
              {v.match && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {matchLabel(v.match)}
                </Typography.Text>
              )}
            </Space>
          )}
        />
        <Table.Column<TvVideo>
          title="Статус"
          width={170}
          render={(_, v) => (
            <Space direction="vertical" size={2}>
              {statusTag(v)}
              {v.publishedAt && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(v.publishedAt).format("DD.MM.YYYY HH:mm")}
                </Typography.Text>
              )}
            </Space>
          )}
        />
        <Table.Column<TvVideo>
          title="Просмотры"
          dataIndex="viewsCount"
          width={110}
        />
        <Table.Column<TvVideo>
          title="Лайки"
          dataIndex="likesCount"
          width={90}
        />
        <Table.Column<TvVideo>
          title="Действия"
          fixed="right"
          width={200}
          render={(_, v) => (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                size="small"
                block
                onClick={() => push(`/tv/videos/${v.id}`)}
              >
                Открыть
              </Button>
              {/* Автовидео не удаляется, а скрывается; уже скрытое — только открыть. */}
              {!(v.source === "AUTO" && v.status === "HIDDEN") && (
                <TvDelete
                  resource="tv-videos"
                  id={v.id}
                  name={v.title}
                  entityAccusative="видео"
                  hide={v.source === "AUTO"}
                  onDeleted={() => tableQueryResult.refetch()}
                />
              )}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
